import React, {useEffect, useMemo, useState} from "react";
import {Container, Spinner} from "react-bootstrap";
import {LevelToggle} from "../../Components/Statistics/FilterBar.jsx";
import InventoryStrategyAssumptions from "../../Components/Statistics/InventoryStrategyAssumptions.jsx";
import InventoryKpiCards from "../../Components/Statistics/InventoryKpiCards.jsx";
import InventoryDetailGrid from "../../Components/Statistics/InventoryDetailGrid.jsx";
import InventoryActionList from "../../Components/Statistics/InventoryActionList.jsx";
import InventoryGroupPalletsChart from "../../Components/Statistics/InventoryGroupPalletsChart.jsx";
import InventoryStrategyHerleitung from "../../Components/Statistics/InventoryStrategyHerleitung.jsx";
import useStatisticsData from "../../Components/Statistics/useStatisticsData.js";

/**
 * Lagerstrategie - Mengenempfehlung je Artikel samt Herleitung, seit August
 * 2026 eine eigene Unterseite (siehe StatisticsController::lagerstrategie()).
 * Ehemals der "Lagerstrategie"-Reiter, jetzt zusätzlich mit der
 * Herleitungsansicht (InventoryStrategyHerleitung) als Vertrauensanker der
 * Seite: sie macht nachvollziehbar, auf welcher Rechengrundlage die
 * empfohlene Menge je Artikel beruht.
 *
 * Anders als Überblick/Sortimente/Artikel/Renner & Penner zeigt diese Seite
 * bewusst NICHT die volle Filterleiste (Zeitraum/Warengruppen/Suche) - der
 * Absatz-Betrachtungszeitraum ist Teil der eigenen Rechenannahmen
 * (InventoryStrategyAssumptions), nicht der globalen Filterleiste, und
 * Warengruppen/Suche wirken auf dieser Seite ausschliesslich über die
 * Spaltenfilter der Detailtabelle - ein zusätzliches, aber wirkungsloses
 * Bedienelement in einer zweiten Filterleiste wäre irreführend. Nur die
 * Auswertungsebene (Artikel/Übernummer) gilt seitenweit und bekommt deshalb
 * denselben Umschalter wie die übrigen Unterseiten.
 */
export default function StatisticsLagerstrategie() {
    const [level, setLevel] = useState("article");
    const [assumptionParams, setAssumptionParams] = useState(null);
    const [groupOptions, setGroupOptions] = useState([]);

    const params = useMemo(() => (assumptionParams ? {...assumptionParams, level} : null), [assumptionParams, level]);

    const {data: groupsData} = useStatisticsData("/statistics/inventory-strategy/groups", {});
    useEffect(() => {
        setGroupOptions((groupsData?.groups || []).filter((g) => g.warengruppe).map((g) => ({value: g.warengruppe, label: g.warengruppe})));
    }, [groupsData]);

    return (
        <Container fluid className="pt-2 pb-4">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                <h1 className="h4 mb-0">Lagerstrategie</h1>
            </div>

            <div className="d-flex flex-column gap-3">
                <div>
                    <div className="small text-muted mb-1">Auswertungsebene</div>
                    <LevelToggle level={level} onLevelChange={setLevel} />
                </div>

                <InventoryStrategyAssumptions onChangeParams={setAssumptionParams} />

                {!assumptionParams ? (
                    <div className="d-flex justify-content-center py-5">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Wird geladen…</span>
                        </Spinner>
                    </div>
                ) : (
                    <>
                        <InventoryKpiCards params={params} />

                        <InventoryDetailGrid params={params} groupOptions={groupOptions} />

                        <div className="row g-3">
                            <div className="col-lg-6">
                                <InventoryActionList kind="offenders" params={params} limit={5} />
                            </div>
                            <div className="col-lg-6">
                                <InventoryActionList kind="understocked" params={params} limit={5} />
                            </div>
                        </div>

                        <InventoryGroupPalletsChart params={params} />

                        <InventoryStrategyHerleitung params={params} />
                    </>
                )}
            </div>
        </Container>
    );
}
